import PageHeader from "@/components/PageHeader";
import Spinner from "@/components/Spinner";
import { useToast } from "@/components/ui/use-toast";
import { Module, PATH_ROOT, PATH_STORES } from "@/constants";
import { useQueryCacheUpdater } from "@/hooks/useQueryCacheUpdater";
import { ApiError } from "@/services/api";
import { ReadStore, WriteStore } from "@/types/store";
import { useMutation, useQuery } from "@tanstack/react-query";
import { exists } from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { StoreService } from "../service/store";
import StoreForm from "./StoreForm";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";

export default function StoreEditor() {
  const { t } = useTranslation();
  const params = useParams();
  const queryStore = () => StoreService.get(params.id as string);
  const { toast } = useToast();
  const { updateQueryDataForExistingItem } = useQueryCacheUpdater(
    Module.Stores,
  );
  const { can_update } = useModulePermissions();
  const storeQuery = useQuery({
    queryKey: [Module.Stores, params.id],
    queryFn: queryStore,
  });

  const mutateStore = async (values: WriteStore): Promise<ReadStore> =>
    StoreService.patch(params.id as string, values);

  const storeMutation = useMutation<ReadStore, ApiError, WriteStore>({
    mutationFn: mutateStore,
    onSuccess: (result) => {
      toast({
        title: t("stores.success.update"),
      });
      updateQueryDataForExistingItem(result);
    },
    onError: ({ errorCode }) => {
      const description = exists(`errors.${errorCode}`)
        ? t(`errors.${errorCode}`)
        : t("errors.unknownError");
      toast({
        title: t("stores.errors.update"),
        description,
        variant: "destructive",
      });
    },
  });

  const breadcrumbItems = useMemo(
    () => [
      { label: t("breadcrumb.home"), href: PATH_ROOT },
      { label: t("stores.title.multiple"), href: PATH_STORES },
      {
        label: t("stores.edit", {
          name: storeQuery.data?.name ?? "...",
        }),
      },
    ],
    [t, storeQuery.data],
  );
  return (
    <>
      <PageHeader breadcrumbItems={breadcrumbItems} />
      {storeQuery.isLoading && (
        <Spinner label={t("stores.loading")} className="w-full h-full " />
      )}
      {!storeQuery.isLoading && (
        <StoreForm
          onSubmit={storeMutation.mutateAsync}
          store={storeQuery.data}
          disabled={storeMutation.isPending || !can_update}
        />
      )}
    </>
  );
}
